import { createTheme } from "@mui/material";

// Font stacks — the faces are self-hosted in public/fonts and declared in
// src/index.css. Fraunces is the display face (headings, wordmark); Switzer
// carries all UI/body text.
export const displayFontFamily =
  '"Fraunces", Georgia, "Times New Roman", serif';
const uiFontFamily =
  '"Switzer", -apple-system, "Segoe UI", Helvetica, Arial, sans-serif';

export const sandooqTheme = createTheme({
  palette: {
    delinquent: {
      main: "#d32f2f",
      light: "#ef5350",
      dark: "#c62828",
      contrastText: "#fff",
    },
    primary: {
      main: "#2E7D32", // Deep green for trust and financial growth
      light: "#4CAF50",
      dark: "#1B5E20",
    },
    secondary: {
      main: "#1976D2", // Professional blue
      light: "#42A5F5",
      dark: "#0D47A1",
    },
    success: {
      main: "#4CAF50",
      light: "#81C784",
      dark: "#388E3C",
    },
    error: {
      main: "#D32F2F",
      light: "#E57373",
      dark: "#B71C1C",
    },
    background: {
      default: "#ffffff",
      paper: "#FFFFFF",
    },
    warning: {
      // Amber, dark enough to carry white text and to stay legible as a text
      // color on white (the old #E8C205 yellow failed contrast both ways).
      main: "#B45309",
      light: "#F59E0B",
      dark: "#92400E",
      contrastText: "#fff",
    },
    text: {
      primary: "#212121",
      secondary: "#757575",
    },
  },
  typography: {
    fontFamily: uiFontFamily,
    h1: {
      fontFamily: displayFontFamily,
      fontWeight: 600,
      fontSize: "3.25rem",
      lineHeight: 1.15,
      letterSpacing: "-0.01em",
    },
    h2: {
      fontFamily: displayFontFamily,
      fontWeight: 600,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontFamily: displayFontFamily,
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.25,
    },
    h4: {
      fontFamily: displayFontFamily,
      fontWeight: 600,
      fontSize: "1.6rem",
      lineHeight: 1.3,
    },
    h5: {
      fontWeight: 650,
      fontSize: "1.25rem",
      lineHeight: 1.35,
    },
    h6: {
      fontWeight: 650,
      fontSize: "1.05rem",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 550,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 550,
      fontSize: "0.9rem",
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1.05rem",
      lineHeight: 1.6,
    },
    // Bumped from MUI defaults (0.875rem / 0.75rem) — a lot of secondary
    // text/labels across the app use these, and the default sizes read
    // small for an older audience.
    body2: {
      fontSize: "0.95rem",
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      letterSpacing: 0,
    },
    caption: {
      fontSize: "0.82rem",
      lineHeight: 1.5,
    },
    overline: {
      fontWeight: 600,
      fontSize: "0.72rem",
      letterSpacing: "0.08em",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          // Border + soft shadow reads calmer than the old floating cards;
          // the hover lift dropped from 4px to 1px because most cards are
          // not clickable and the jump read as noise.
          border: "1px solid rgba(27, 94, 32, 0.08)",
          boxShadow: "0 2px 12px rgba(16, 24, 40, 0.06)",
          transition:
            "transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 16px rgba(16, 24, 40, 0.09)",
          },
        },
      },
    },
  },
});
